import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useStateMachine } from 'little-state-machine'
import { Store } from './Scale'
import { swicthContrast } from './actions'
import { InquiryFormType } from '../../utils/constants'
import { Pdf } from './Pdf/Pdf'

// @ts-ignore @todo add apprioprate declaration file
import contrastImg from './images/contrast.svg'

// @ts-ignore @todo add apprioprate declaration file
import printer from './images/print.svg'
// @ts-ignore @todo add apprioprate declaration file
import { SpeLevel } from '../../utils/constants'
import { Hidden } from '../../styles/Shared'

type Props = {
  type: InquiryFormType['name']
  isDiagnostician: boolean
  code?: string
  speLevel: SpeLevel
  language?: string
}

const Layout: React.FC<Props> = ({
  children,
  type,
  isDiagnostician,
  code,
  speLevel,
  language,
}) => {
  const { t } = useTranslation('translation')
  const {
    action,
    state: { inquery },
  } = useStateMachine<Store>(swicthContrast)
  const setColorMode = React.useCallback((isContrast: boolean) => {
    const root = window.document.documentElement

    root.style.setProperty(
      '--color-primary',
      isContrast ? '#00F6FF' : '#b5303e'
    )
    root.style.setProperty('--color-background', isContrast ? 'black' : 'white')
    root.style.setProperty('--color-text', isContrast ? 'white' : 'black')
    root.style.setProperty(
      '--color-text-button',
      isContrast ? 'black' : 'white'
    )
    root.style.setProperty(
      '--color-background-dark',
      isContrast ? '#232323' : '#bbb'
    )
    root.style.setProperty(
      '--color-background-light',
      isContrast ? '#656565' : '#eaeaea'
    )
  }, [])

  useEffect(() => {
    setColorMode(inquery.contrast)
  }, [inquery.contrast])
  const languages = ['PL', 'EN', 'UA', 'VI']
  const formAlreadySent = isDiagnostician && Boolean(code) && Boolean(language)
  const [isActiveDoc, setActiveDoc] = useState(false)

  const handleDocsClick = () => {
    setActiveDoc((isActiveDoc) => !isActiveDoc)
  }

  return (
    <StyledWrapper isDiagnostician={isDiagnostician}>
      <Header>
        <h1>
          {t('inquiry')} {type}
        </h1>

        <Right>
          {speLevel === 12 &&
          (type.includes('KAPP_S_P(R)') || type.includes('KAPP_S_S(R)')) ? (
            <React.Fragment>
              <IconButton onClick={() => handleDocsClick()}>
                <Hidden>Pobierz skalę w formacie .pdf</Hidden>
                <img alt="" src={printer} aria-hidden="true"></img>
              </IconButton>
            </React.Fragment>
          ) : (
            <DownloadInquiry type={type} />
          )}
          {!isDiagnostician && (
            <IconButton onClick={action}>
              <Hidden>Pobierz skalę</Hidden>
              <img alt="" src={contrastImg} aria-hidden="true"></img>
            </IconButton>
          )}
        </Right>
      </Header>
      {isActiveDoc && (
        <LangSelection>
          <img alt="" src={printer} aria-hidden="true"></img>
          {languages.map((language, index) => {
            return (
              <LanguageSpecifiedDoc
                key={index}
                download
                href={
                  language === 'PL'
                    ? `../../forms/${type}.pdf`
                    : `../../forms/${language.toLowerCase()}_${type}.pdf`
                }
              >
                {language}
              </LanguageSpecifiedDoc>
            )
          })}
        </LangSelection>
      )}
      <Main>{children}</Main>
    </StyledWrapper>
  )
}

type DownloadProps = {
  type: InquiryFormType['name']
}

function DownloadInquiry({ type }: DownloadProps) {
  if (!type) return null
  return (
    <IconAnchor download href={`../../forms/${type}.pdf`}>
      <Hidden>Pobierz skalę</Hidden>
      <img alt="" src={printer} aria-hidden="true"></img>
    </IconAnchor>
  )
}

const Right = styled.div`
  margin-left: auto;
  display: flex;
`

export const Footer = styled.footer`
  padding: 1rem;
  display: flex;
  width: 100%;
  display: flex;
  justify-content: flex-end;
`

export const Button = styled.button`
  background: var(--color-primary);
  color: var(--color-text-button);
  border-radius: 0.25rem;
  border: none;
  padding: 0.5rem 1rem;
  margin-left: 0.2rem;
`

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: clamp(30rem, 800px, 50vw);
`

export const Label = styled.label`
  font-size: 1.5rem;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  align-items: center;
  justify-content: center;

  input,
  select {
    min-width: 250px;
    font-size: 1rem;
    margin-top: 1rem;
  }
  textarea {
    border-radius: 1em;
    min-width: 350px;
    min-height: 250px;
    font-size: 1rem;
    margin-top: 1rem;
    background: var(--color-background);
    color: var(--color-text);
  }
`

export const Section = styled.section`
  padding: 1rem;
  display: flex;
  min-height: 100%;
  flex: 1 1;
  flex-direction: column;
  align-items: center;
`

const Main = styled.main``

export const Header = styled.header`
  display: flex;
  background: var(--color-primary);
  color: var(--color-text);
  color: var(--color-background);
  > h1 {
    margin: 0;
    padding: 0.5rem;
  }
`

const IconButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  background-color: transparent;
  font-weight: 500;
  border: 1px solid transparent;
  height: auto;
  padding: 0 1rem;
  cursor: pointer;
  &:focus {
    outline: none;
  }
`

export const IconAnchor = styled.a`
  display: flex;
  cursor: pointer;
  padding: 0 1rem;
  align-items: center;
  background: none;
  background-color: transparent;
  font-weight: 500;
  border: 1px solid transparent;
  height: auto;
  &:first-of-type {
    margin-left: auto;
  }
`
export const LangSelection = styled.div`
  display: flex;
  color: var(--color-background);
  margin-left: auto;
  width: 180px;
  padding: 0.1rem;
  justify-content: space-evenly;
  background: var(--color-primary);
`
export const LanguageSpecifiedDoc = styled.a`
  color: inherit;
  text-decoration: none;
  padding: 0 0.4rem;
`

const StyledWrapper = styled.div`
  background: var(--color-background);
  color: var(--color-text);
  width: ${(props) => (props.isDiagnostician ? '100vw' : '100%')};
`

export const FieldWrapper = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  flex-direction: column;
`

export default Layout
